import { Link } from "react-router-dom";
import { ArrowRight, Truck, Building2, Palette, Sun, Home as HomeIcon, PaintBucket, Sparkles, Check } from "lucide-react";

const services = [
  { slug: "commercial-vehicle-wraps", icon: Truck, name: "Commercial Vehicle Wraps", tagline: "A 24/7 salesperson on wheels.", body: "Custom-designed full and partial wraps for contractors, service businesses, and owner-operators. Every wrap is engineered around your brand — not stamped from a template.", bullets: ["3M / Avery cast materials", "Custom design included", "1-year craftsmanship warranty"] },
  { slug: "fleet-wraps", icon: Building2, name: "Fleet Wraps", tagline: "One identity. Every vehicle.", body: "From two vans to twenty, we keep your fleet visually consistent, easy to update, and instantly recognizable across Northern Arizona.", bullets: ["Scalable brand systems", "Rolling install scheduling", "Volume pricing available"] },
  { slug: "automotive-window-tint", icon: Sun, name: "Automotive Window Tint", tagline: "Comfort, privacy, protection.", body: "Ceramic and carbon film options that cut heat, block UV, and finish clean. Legal Arizona shades, installed dust-free.", bullets: ["Ceramic heat rejection", "Lifetime film warranty", "Legal AZ shades"] },
  { slug: "residential-window-tint", icon: HomeIcon, name: "Residential Window Tint", tagline: "Your home. Cooler. Calmer.", body: "Lower cooling costs, protect furniture and floors from UV fade, and add privacy without sacrificing light.", bullets: ["UV & heat rejection", "Fade protection", "Multiple aesthetic finishes"] },
  { slug: "commercial-window-tint", icon: Building2, name: "Commercial Window Tint", tagline: "Better workspace. Lower bills.", body: "Office fronts, storefronts, retail — energy-efficient window films that also improve customer comfort and curb appeal.", bullets: ["Energy-saving film", "Storefront-grade durability", "After-hours install available"] },
  { slug: "signs-and-banners", icon: PaintBucket, name: "Signs & Banners", tagline: "Physical branding, done right.", body: "Storefront signs, event banners, yard signs, real estate branding, trade-show visuals — designed and produced in-house.", bullets: ["Design + print + install", "Indoor / outdoor grade", "Fast turnaround"] },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* Hero */}
      <section className="pt-40 pb-16">
        <div className="wrap">
          <div className="eyebrow eyebrow-red">Services · 928 Wraps</div>
          <h1 className="h-display text-6xl md:text-8xl uppercase mt-4 max-w-5xl">
            One studio. Every <span className="italic font-normal">visual</span> touch-point.
          </h1>
          <p className="mt-6 text-lg text-neutral-600 max-w-2xl">
            Wraps, tint, signs, graphics — we handle every surface your customers will ever see. Design, print, and install all under one roof, with one accountable owner: Dave.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/mockup" data-testid="services-hero-cta" className="btn-red">Get My FREE Mockup <ArrowRight size={16} /></Link>
            <Link to="/portfolio" className="btn-ghost">See recent work <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-32">
        <div className="wrap grid md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <Link key={s.name} to={`/services/${s.slug}`} data-testid={`service-detail-${i}`} data-track="service_card_click" data-track-location={`services_grid_${s.slug}`} className="flat-card rounded-3xl p-8 md:p-10 block group">
              <div className="flex items-center justify-between">
                <div className="w-14 h-14 rounded-2xl bg-white border border-neutral-200 grid place-items-center">
                  <s.icon size={26} strokeWidth={1.5} />
                </div>
                <span className="eyebrow">0{i+1}</span>
              </div>
              <h3 className="h-display text-3xl md:text-4xl uppercase mt-8 leading-tight">{s.name}</h3>
              <div className="eyebrow eyebrow-red mt-2">{s.tagline}</div>
              <p className="mt-4 text-neutral-600 leading-relaxed">{s.body}</p>
              <ul className="mt-6 space-y-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-neutral-800">
                    <Check size={16} className="text-[var(--red)]" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink)] group-hover:text-[var(--red)] transition-colors">
                Explore {s.name} <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="pb-32">
        <div className="wrap">
          <div className="bg-[var(--ink)] text-white rounded-3xl p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <div className="eyebrow eyebrow-red">Not sure which service you need?</div>
              <h3 className="h-display text-4xl md:text-6xl uppercase mt-3">Let&apos;s figure it out <span className="italic font-normal text-neutral-500">together.</span></h3>
              <p className="mt-4 text-neutral-400 max-w-xl">Send a quick brief through our mockup form. Dave will personally walk you through what will actually move the needle for your business.</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/mockup" data-testid="services-cta" className="btn-red">Start free mockup <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
